import React from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../../api';
import MarkdownRenderer from '../ui/MarkdownRenderer';

interface AiCommentaryProps {
  metricCode?: string;
  commentary?: string;
  loading?: boolean;
  error?: string;
  className?: string;
}

export default function AiCommentary({
  metricCode,
  commentary: propCommentary,
  loading: propLoading,
  error: propError,
  className,
}: AiCommentaryProps) {
  const shouldFetch = !!metricCode && !propCommentary;

  const { data, isLoading: queryLoading, error: queryError, refetch } = useQuery<{ commentary: string }>({
    queryKey: ['ai-commentary', metricCode],
    queryFn: () => api.get(`/metrics/financial/commentary/${metricCode}`).then(r => r.data),
    enabled: shouldFetch,
    staleTime: 5 * 60 * 1000,
  });

  const isLoading = propLoading || (shouldFetch && queryLoading);
  const errorMsg = propError || (queryError ? (queryError as Error).message : null);
  const text = propCommentary || data?.commentary;

  return (
    <div
      className={className}
      style={{
        background: 'var(--bg-surface)',
        border: '1px solid var(--border-default)',
        borderRadius: 'var(--radius-md)',
        padding: '16px 20px',
      }}
    >
      {/* Header */}
      <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--accent)', marginBottom: 12 }}>
        ✦ AI Commentary
      </div>

      {/* Loading */}
      {isLoading && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ height: 14, width: '90%', background: 'var(--bg-elevated)', borderRadius: 4, animation: 'pulse 1.5s ease-in-out infinite' }} />
          <div style={{ height: 14, width: '75%', background: 'var(--bg-elevated)', borderRadius: 4, animation: 'pulse 1.5s ease-in-out infinite' }} />
          <div style={{ height: 14, width: '60%', background: 'var(--bg-elevated)', borderRadius: 4, animation: 'pulse 1.5s ease-in-out infinite' }} />
        </div>
      )}

      {/* Error */}
      {!isLoading && errorMsg && (
        <div style={{ fontSize: 13, color: 'var(--text-tertiary)' }}>
          Unable to load commentary
          <button
            onClick={() => refetch()}
            style={{
              marginLeft: 8,
              background: 'none',
              border: 'none',
              color: 'var(--accent)',
              fontSize: 13,
              cursor: 'pointer',
              textDecoration: 'underline',
              fontFamily: 'var(--font-sans)',
              padding: 0,
            }}
          >
            Retry
          </button>
        </div>
      )}

      {/* Content */}
      {!isLoading && !errorMsg && text && (
        <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
          <MarkdownRenderer content={text} />
        </div>
      )}

      {/* Empty */}
      {!isLoading && !errorMsg && !text && (
        <p style={{ fontSize: 13, color: 'var(--text-tertiary)', margin: 0 }}>
          No commentary available.
        </p>
      )}
    </div>
  );
}
